// Reference-image attach row shared by both prompt composers (the workspace
// Prompt popover and the AssistantDrawer's ConversationHistory). Renders a
// paperclip attach button + removable filename chips + an inline error line.
import { useRef } from "react";

export interface ReferenceAsset {
  id: string;
  filename: string;
}

interface ReferenceBarProps {
  references: ReferenceAsset[];
  uploading: boolean;
  error?: string | null;
  max: number;
  onAttach: (files: FileList | null) => void;
  onRemove: (id: string) => void;
}

function PaperclipIcon() {
  return (
    <svg
      className="h-3.5 w-3.5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21.44 11.05l-9.19 9.19a5 5 0 0 1-7.07-7.07l9.19-9.19a3 3 0 0 1 4.24 4.24l-9.2 9.19a1 1 0 0 1-1.41-1.41l8.49-8.49"
      />
    </svg>
  );
}

export function ReferenceBar({
  references,
  uploading,
  error,
  max,
  onAttach,
  onRemove,
}: ReferenceBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const atMax = references.length >= max;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || atMax}
          title={
            atMax
              ? `Up to ${max} reference images`
              : "Attach reference images for the model to view"
          }
          className="flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-xs font-medium text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
        >
          <PaperclipIcon />
          {uploading ? "Uploading…" : "Reference"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,video/mp4,video/webm,video/quicktime"
          multiple
          className="hidden"
          onChange={(e) => {
            onAttach(e.target.files);
            e.target.value = "";
          }}
        />
        {references.map((r) => (
          <span
            key={r.id}
            className="flex max-w-[10rem] items-center gap-1 rounded-full bg-surface-3 px-2 py-0.5 text-xs text-ink-2"
          >
            <span className="truncate" title={r.filename}>
              {r.filename}
            </span>
            <button
              type="button"
              onClick={() => onRemove(r.id)}
              aria-label={`Remove ${r.filename}`}
              className="shrink-0 text-ink-3 transition-colors hover:text-ink"
            >
              <svg
                className="h-3 w-3"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                aria-hidden
              >
                <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
      </div>
      {error && <div className="text-xs text-danger">{error}</div>}
    </div>
  );
}
